import React, { ReactNode, ReactElement } from "react"
import Button from "components/Buttons/Button"
import CancelButton from "components/Buttons/CancelButton"
import Modal from "components/dialogs/Modal"
import { ButtonProps, Text } from "@chakra-ui/react"

type Props = {
  children: ReactElement;
  confirmationTitle?: string;
  confirmationText?: ReactNode | string;
  confirmButtonLabel?: string;
  confirmButtonColorScheme?: ButtonProps["colorScheme"]
  onClickConfirm?: () => void | Promise<void>;
  skipIf?: boolean;
  stopChildClickPropagation?: boolean;
}

const isThenable = (value: unknown): value is Promise<unknown> => !!value
  && (typeof value === "object" || typeof value === "function")
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  && typeof (value as any).then === "function"

const Confirm = ({
  children,
  confirmationTitle = "Delete",
  confirmationText = "Are you sure?",
  confirmButtonLabel = "OK",
  confirmButtonColorScheme = "button.danger",
  onClickConfirm = () => {},
  skipIf,
  stopChildClickPropagation,
}: Props) => {
  const [open, setOpen] = React.useState(false)
  const [confirming, setConfirming] = React.useState(false)

  const {
    props: {
      onClick: onClickChildren = () => {},
    },
  } = children

  const handleClickOpen = (e) => {
    if (skipIf) {
      const result = onClickConfirm()
      if (isThenable(result)) {
        result.catch(() => {})
      }
    } else {
      setOpen(true)
    }
    if (stopChildClickPropagation) {
      e.stopPropagation()
    }
  }

  const handleClose = () => {
    if (confirming) return
    setOpen(false)
  }

  const handleConfirm = async () => {
    if (confirming) return

    const confirmResult = onClickConfirm()
    const childResult = onClickChildren()

    // Backwards-compatible behavior:
    // - If handlers don't return a promise, close immediately (old behavior).
    // - If either returns a promise, show loading and keep open until complete.
    if (!isThenable(confirmResult) && !isThenable(childResult)) {
      setOpen(false)
      return
    }

    setConfirming(true)
    try {
      await Promise.all([Promise.resolve(confirmResult), Promise.resolve(childResult)])
    } catch (e) {
      // Swallow to avoid unhandled promise rejections from async handlers.
    } finally {
      setConfirming(false)
      setOpen(false)
    }
  }

  return (
    <>
      <children.type {...children.props} onClick={handleClickOpen} />
      <Modal
        size="lg"
        show={open}
        hide={handleClose}
        title={confirmationTitle}
        actions={[
          <CancelButton onClick={handleClose} isDisabled={confirming} />,
          <Button
            onClick={handleConfirm}
            colorScheme={confirmButtonColorScheme}
            isLoading={confirming}
            loadingText={confirmButtonLabel}
          >{confirmButtonLabel}
          </Button>,
        ]}
      >
        <Text size="xl">{confirmationText}</Text>
      </Modal>
    </>
  )
}

export default Confirm
